<template>
  <BaseModal id="label-creation" :value="value" @input="changeStatus">
    <template #header>
      {{ $t("message.return_label.label_creation") }}
    </template>
    <template #default>
      <div class="flex justify-center px-4 py-2 flex flex-col items-center">
        <div
          class="flex flex-col w-full"
          v-if="!loadingRates && address && editAddress"
        >
          <label class="text-sm font-semibold mb-2">
            {{ $t("message.address_form.name") }}
          </label>
          <input
            v-model="address.name"
            type="text"
            class="input-field mb-4"
            :placeholder="$t('message.address_form.name')"
          />
          <label class="text-sm font-semibold mb-2">
            {{ $t("message.address_form.street1") }}
          </label>
          <input
            v-model="address.street1"
            type="text"
            class="input-field mb-4"
            :placeholder="$t('message.address_form.street1')"
          />
          <label class="text-sm font-semibold mb-2">
            {{ $t("message.address_form.street2") }}
          </label>
          <input
            v-model="address.street2"
            type="text"
            class="input-field mb-4"
            :placeholder="$t('message.address_form.street2')"
          />
          <label class="text-sm font-semibold mb-2">
            {{ $t("message.address_form.city") }}
          </label>
          <input
            v-model="address.city"
            type="text"
            class="input-field mb-4"
            :placeholder="$t('message.address_form.city')"
          />
          <label class="text-sm font-semibold mb-2">
            {{ $t("message.address_form.state") }}
          </label>
          <select
            class="regular full bordered"
            type="text"
            name="state"
            v-model="address.state"
          >
            <option
              v-for="state in statesFixed"
              :value="state.shortCode"
              :key="state.shortCode"
              >{{ state.label }}</option
            >
          </select>
          <label class="text-sm font-semibold mb-2">
            {{ $t("message.address_form.postal_code") }}
          </label>
          <input v-model="address.postalCode" type="text" />
          <label class="text-sm font-semibold mb-2">
            {{ $t("message.order.phone") }}
          </label>
          <input
            v-model="address.phone"
            type="text"
            class="input-field mb-4"
            :placeholder="$t('message.order.phone')"
          />
          <label class="text-sm font-semibold mb-2">
            {{ $t("message.address_form.country") }}
          </label>
          <input
            v-model="address.country"
            type="text"
            class="input-field mb-4"
            :placeholder="$t('message.address_form.country')"
            disabled
          />
          <button
            class="btn-dark-button flex justify-center text-center items-center my-10"
            @click="recalculateRates"
          >
            <span v-if="!creatingLabel">
              {{ $t("message.return_label.recalculate_rates") }}
            </span>
          </button>
        </div>
        <div v-else-if="!loadingRates && address">
          {{ $t("message.return_label.address") }}: {{ address.name }},
          {{ address.street1 }}, {{ address.street2 }}, {{ address.city }},
          {{ address.state }}, {{ address.postalCode }}, {{ address.country }}
          <button
            class="btn-dark-button flex justify-center text-center items-center my-10"
            @click="editAddress = true"
            v-if="!creatingLabel"
          >
            {{ $t("message.customer.edit_address") }}
          </button>
        </div>
        <BaseMiniSpinner v-if="loadingRates" />
        <div v-else-if="labelCost || packageInfo || errorMessage">
          <!-- Error message -->
          <div
            v-if="errorMessage"
            class="w-full mb-4 bg-red-100 border border-red-400 text-red-800 px-3 py-2 rounded text-sm"
          >
            <p class="font-semibold">{{ errorMessage }}</p>
            <details v-if="debugInfo" class="mt-2">
              <summary class="text-xs text-red-600 cursor-pointer"
                >Show details</summary
              >
              <div class="mt-1 text-xs text-red-700 bg-red-50 p-2 rounded">
                <p>
                  From: {{ debugInfo.fromCity }}, {{ debugInfo.fromState }}
                  {{ debugInfo.fromPostalCode }}
                </p>
                <p>To: {{ debugInfo.toState }} {{ debugInfo.toPostalCode }}</p>
                <p>
                  Carrier: {{ debugInfo.carrier }} / {{ debugInfo.service }}
                </p>
                <p>
                  Package: {{ debugInfo.weight }}oz — {{ debugInfo.length }}" x
                  {{ debugInfo.width }}" x {{ debugInfo.height }}"
                </p>
              </div>
            </details>
          </div>

          <!-- Warnings -->
          <div v-if="warnings && warnings.length > 0" class="w-full mb-4">
            <div
              v-for="(warning, idx) in warnings"
              :key="idx"
              class="text-sm bg-yellow-100 border border-yellow-400 text-yellow-800 px-3 py-2 rounded mb-1"
            >
              ⚠️ {{ warning }}
            </div>
          </div>

          <!-- Package dimensions -->
          <div
            v-if="packageInfo"
            class="w-full mb-4 border rounded p-3 bg-gray-50"
          >
            <div class="flex justify-between items-center mb-2">
              <h4 class="font-semibold text-sm">Package Dimensions</h4>
              <button
                class="text-xs text-blue-600 underline"
                @click="editDimensions = !editDimensions"
                v-if="!creatingLabel"
              >
                {{ editDimensions ? "Done" : "Edit" }}
              </button>
            </div>
            <div v-if="editDimensions" class="grid grid-cols-2 gap-2">
              <div>
                <label class="text-xs text-gray-600">Weight (oz)</label>
                <input
                  v-model.number="packageInfo.weight"
                  type="number"
                  step="0.1"
                  class="input-field w-full"
                />
              </div>
              <div>
                <label class="text-xs text-gray-600">Length (in)</label>
                <input
                  v-model.number="packageInfo.packageLength"
                  type="number"
                  step="0.1"
                  class="input-field w-full"
                />
              </div>
              <div>
                <label class="text-xs text-gray-600">Width (in)</label>
                <input
                  v-model.number="packageInfo.packageWidth"
                  type="number"
                  step="0.1"
                  class="input-field w-full"
                />
              </div>
              <div>
                <label class="text-xs text-gray-600">Height (in)</label>
                <input
                  v-model.number="packageInfo.packageHeight"
                  type="number"
                  step="0.1"
                  class="input-field w-full"
                />
              </div>
              <div class="col-span-2 mt-2">
                <button
                  class="btn-info-button w-full text-sm"
                  @click="recalculateRates"
                  :disabled="loadingRates"
                >
                  Recalculate with new dimensions
                </button>
              </div>
            </div>
            <div v-else class="text-sm text-gray-700">
              <p>
                Weight: {{ packageInfo.weight }} oz |
                {{ packageInfo.packageLength }}" x
                {{ packageInfo.packageWidth }}" x
                {{ packageInfo.packageHeight }}"
              </p>
            </div>
          </div>

          <p v-if="labelCost">
            {{ $t("message.return_label.carrier") }}:
            {{ labelCost.serviceName }}
          </p>
          <p v-if="labelCost">
            {{ $t("message.return_label.price") }}: ${{ labelCost.totalAmount }}
          </p>
        </div>
        <!-- Always-visible recalculate button -->
        <button
          v-if="
            !loadingRates &&
              !editAddress &&
              (packageInfo || labelCost || errorMessage)
          "
          class="btn-info-button w-full text-sm my-2"
          @click="recalculateRates"
          :disabled="loadingRates"
        >
          Recalculate rates
        </button>
        <button
          v-if="labelCost"
          class="btn-dark-button flex justify-center text-center items-center my-10"
          @click="createLabel"
        >
          <span v-if="!creatingLabel">
            {{ $t("message.return_label.create_label") }}
          </span>
          <BaseMiniSpinner v-else />
        </button>
      </div>
    </template>
  </BaseModal>
</template>
<script>
import {
  createReturnLabel,
  calculateReturnLabel
} from "@/api/store/ReturnsService.js";
const states = require("@/constants/states.json");

export default {
  props: {
    value: {
      type: Boolean,
      required: true
    },
    returnId: {
      type: String,
      required: true
    },
    labels: {
      type: Array,
      required: true
    },
    originalAddress: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      creatingLabel: false,
      loadingRates: false,
      labelCost: null,
      address: null,
      editAddress: false,
      packageInfo: null,
      warnings: null,
      editDimensions: false,
      errorMessage: null,
      debugInfo: null
    };
  },
  mounted() {
    this.address = {
      ...this.originalAddress
    };
  },
  watch: {
    value(val) {
      if (val) {
        this.calculateLabel(this.originalAddress);
      }
    }
  },
  methods: {
    changeStatus(newStatus) {
      this.$emit("input", newStatus);
    },
    async calculateLabel(address, packageOverride = null) {
      this.loadingRates = true;
      this.labelCost = null;
      this.errorMessage = null;
      this.debugInfo = null;
      if (!packageOverride) {
        this.packageInfo = null;
        this.warnings = null;
      }
      try {
        const calculations = await calculateReturnLabel(
          this.returnId,
          address,
          packageOverride
        );
        this.labelCost = calculations.rates[0];
        if (!packageOverride) {
          this.packageInfo = calculations.packageDetails || null;
          this.warnings = calculations.warnings || [];
        }
      } catch (error) {
        const errorData = error.response?.data;
        const message =
          errorData?.error?.message ||
          this.$t("message.return_label.error_calculating_label");
        this.errorMessage = message;
        // Extract debug info, packageDetails, warnings from error response
        if (errorData?.debug) {
          this.debugInfo = errorData.debug;
        }
        if (errorData?.packageDetails) {
          this.packageInfo = errorData.packageDetails;
        }
        if (errorData?.warnings) {
          this.warnings = errorData.warnings;
        }
        this.$toast.open({
          message: message,
          type: "error",
          position: "top-right"
        });
        console.error("calculateLabel error:", errorData || error.message);
      }
      this.loadingRates = false;
    },
    async createLabel() {
      const phoneDigits = (this.address?.phone || "").replace(/\D/g, "");
      if (phoneDigits.length < 10) {
        this.$toast.open({
          message: this.$t("message.return_label.invalid_phone"),
          type: "error",
          position: "top-right"
        });
        this.editAddress = true;
        return;
      }
      try {
        this.creatingLabel = true;
        const label = await createReturnLabel(
          this.returnId,
          this.address,
          this.packageInfo
        );
        this.$emit("labelCreated", label);
        window.open(label.labelUrl, "_blank");
        this.$toast.open({
          message: this.$t("message.return_label.label_created"),
          type: "success",
          position: "top-right"
        });
      } catch (error) {
        const message =
          error.response?.data?.error?.message ||
          this.$t("message.return_label.error_requesting_label");
        this.$toast.open({
          message: message,
          type: "error",
          position: "top-right"
        });
        console.error(
          "createLabel error:",
          error.response?.data || error.message
        );
      }
      this.creatingLabel = false;
    },
    async recalculateRates() {
      await this.calculateLabel(this.address, this.packageInfo);
      this.editAddress = false;
      this.editDimensions = false;
    }
  },
  computed: {
    statesFixed() {
      const keys = Object.keys(states);
      return keys.map(key => {
        return {
          shortCode: key,
          label: states[key]
        };
      });
    }
  }
};
</script>
<style>
#label-creation.modal .modal-content {
  max-width: 500px !important;
  width: 100%;
  min-height: 150px;
  height: auto !important;
}
</style>
